import React, { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyCard = () => ({ title: '', text: '', icon: '✨' });
const emptyTeam = () => ({ name: '', role: '', summary: '' });
const emptyMilestone = () => ({ year: '', title: '', description: '', image_url: '' });
const emptyAward = () => ({ year: '', title: '', image_url: '' });
const emptyMedia = () => ({ outlet: '', title: '', image_url: '' });

const defaultForm = {
  about_hero_title: '',
  about_hero_text: '',
  about_hero_image: '',
  vision_title: '',
  vision_body: '',
  mission_title: '',
  mission_body: '',
  founder_name: '',
  founder_title: '',
  founder_story: '',
  home_about_cards: [emptyCard(), emptyCard(), emptyCard()],
  values_cards: [emptyCard(), emptyCard(), emptyCard(), emptyCard()],
  team_members: [emptyTeam(), emptyTeam(), emptyTeam(), emptyTeam()],
  milestones: [emptyMilestone(), emptyMilestone(), emptyMilestone()],
  awards: [emptyAward()],
  media_mentions: [emptyMedia()],
};

const listConfigs = {
  home_about_cards: { title: 'Home About Cards', create: emptyCard, imageField: null },
  values_cards: { title: 'Values — "What We Believe" (About page)', create: emptyCard, imageField: null },
  team_members: { title: 'Team Members', create: emptyTeam, imageField: null },
  milestones: { title: 'Milestones', create: emptyMilestone, imageField: 'image_url' },
  awards: { title: 'Awards', create: emptyAward, imageField: 'image_url' },
  media_mentions: { title: 'Media Mentions', create: emptyMedia, imageField: 'image_url' },
};

export const BrandContentEditor = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [message, setMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [publishedContent, setPublishedContent] = useState(defaultForm);
  const [meta, setMeta] = useState({ draft_updated_at: null, published_at: null });
  const [uploadingKey, setUploadingKey] = useState('');
  const fileInputRefs = useRef({});

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/brand-pages/admin`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setForm({ ...defaultForm, ...data.content });
        setPublishedContent({ ...defaultForm, ...(data.published_content || data.content) });
        setMeta({ draft_updated_at: data.draft_updated_at, published_at: data.published_at });
      }
    } catch (err) {
      console.error(err);
      setMessage('Failed to load brand content.');
    }
    setLoading(false);
  };

  useEffect(() => {
    const id = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(id);
  }, [token]);

  const previewContent = form;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateArrayField = (field, index, key, value) => {
    setForm((prev) => {
      const updated = [...prev[field]];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [field]: updated };
    });
  };

  const moveItem = (field, index, direction) => {
    setForm((prev) => {
      const arr = [...prev[field]];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return { ...prev, [field]: arr };
    });
  };

  const addItem = (field) => setForm((prev) => ({ ...prev, [field]: [...prev[field], listConfigs[field].create()] }));
  const removeItem = (field, index) => setForm((prev) => ({ ...prev, [field]: prev[field].filter((_, idx) => idx !== index) }));

  const uploadImage = async (file, onSuccess, uploadId) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingKey(uploadId);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/image-upload/feature-image`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        onSuccess(`${API_URL}${data.url}`);
        setMessage('Image uploaded successfully.');
      } else {
        setMessage(data.detail || 'Image upload failed.');
      }
    } catch (err) {
      console.error(err);
      setMessage('Image upload failed.');
    }
    setUploadingKey('');
  };

  const saveDraft = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/brand-pages/admin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      setMessage(data.success ? 'Draft saved successfully.' : 'Failed to save draft.');
      if (data.success) await load();
    } catch (err) {
      console.error(err);
      setMessage('Failed to save draft.');
    }
    setSaving(false);
  };

  const publish = async () => {
    setPublishing(true);
    setMessage('');
    try {
      await saveDraft();
      const res = await fetch(`${API_URL}/api/brand-pages/admin/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMessage(data.success ? 'Draft published live successfully.' : 'Failed to publish.');
      if (data.success) await load();
    } catch (err) {
      console.error(err);
      setMessage('Failed to publish.');
    }
    setPublishing(false);
  };

  const revert = async () => {
    setReverting(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/brand-pages/admin/revert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMessage(data.success ? 'Draft reverted to published version.' : 'Failed to revert draft.');
      if (data.success) await load();
    } catch (err) {
      console.error(err);
      setMessage('Failed to revert draft.');
    }
    setReverting(false);
  };

  const renderImageUpload = ({ value, onChange, uploadId, label }) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-slate-700">{label}</label>
      {value ? (
        <img src={value} alt={label} className="h-28 w-full rounded-xl object-cover border border-slate-200" />
      ) : (
        <div className="flex h-28 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400">No image uploaded</div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => fileInputRefs.current[uploadId]?.click()}
          className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
          data-testid={`${uploadId}-upload-button`}
        >
          {uploadingKey === uploadId ? 'Uploading...' : 'Upload Image'}
        </button>
        {value && (
          <button onClick={() => onChange('')} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700">Remove</button>
        )}
      </div>
      <input
        ref={(el) => { fileInputRefs.current[uploadId] = el; }}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => uploadImage(e.target.files?.[0], onChange, uploadId)}
      />
    </div>
  );

  const renderListEditor = (field, items) => {
    const config = listConfigs[field];
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm" data-testid={`brand-list-${field}`}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 className="text-lg font-bold text-slate-900">{config.title}</h4>
          <button onClick={() => addItem(field)} className="rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">Add Item</button>
        </div>
        <div className="space-y-4">
          {items.map((item, idx) => (
            <div key={`${field}-${idx}`} className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`${field}-item-${idx}`}>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <p className="text-sm font-semibold text-slate-700">Item {idx + 1}</p>
                <div className="flex gap-2">
                  <button onClick={() => moveItem(field, idx, -1)} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold">↑</button>
                  <button onClick={() => moveItem(field, idx, 1)} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold">↓</button>
                  {items.length > 1 && <button onClick={() => removeItem(field, idx)} className="rounded-lg border border-red-300 px-3 py-1 text-xs font-semibold text-red-600">Delete</button>}
                </div>
              </div>

              {'name' in item && <input value={item.name} onChange={(e) => updateArrayField(field, idx, 'name', e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Name" />}
              {'title' in item && <input value={item.title} onChange={(e) => updateArrayField(field, idx, 'title', e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Title" />}
              {'year' in item && <input value={item.year} onChange={(e) => updateArrayField(field, idx, 'year', e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Year" />}
              {'outlet' in item && <input value={item.outlet} onChange={(e) => updateArrayField(field, idx, 'outlet', e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Outlet" />}
              {'role' in item && <input value={item.role} onChange={(e) => updateArrayField(field, idx, 'role', e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Role" />}
              {'icon' in item && <input value={item.icon} onChange={(e) => updateArrayField(field, idx, 'icon', e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Icon / Emoji" />}
              {'summary' in item && <textarea value={item.summary} onChange={(e) => updateArrayField(field, idx, 'summary', e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Summary" />}
              {'text' in item && <textarea value={item.text} onChange={(e) => updateArrayField(field, idx, 'text', e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Text" />}
              {'description' in item && <textarea value={item.description} onChange={(e) => updateArrayField(field, idx, 'description', e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="Description" />}

              {config.imageField && renderImageUpload({
                value: item[config.imageField],
                onChange: (url) => updateArrayField(field, idx, config.imageField, url),
                uploadId: `${field}-${idx}`,
                label: 'Image'
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) return <div className="p-6 text-sm text-gray-500" data-testid="brand-content-editor-loading">Loading brand editor...</div>;

  return (
    <div className="space-y-6" data-testid="brand-content-editor">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-orange-600 p-6 text-white shadow-lg">
        <h3 className="text-2xl font-black">Brand Page Content Editor</h3>
        <p className="mt-2 text-sm text-white/80">Direct image upload, preview, draft/publish controls, and reorder actions for all brand blocks.</p>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm" data-testid="brand-publish-controls">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="text-sm text-slate-600">
            <p><strong>Draft Updated:</strong> {meta.draft_updated_at || 'Not saved yet'}</p>
            <p><strong>Published:</strong> {meta.published_at || 'Not published yet'}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setShowPreview((prev) => !prev)} className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700" data-testid="brand-preview-toggle-button">
              {showPreview ? 'Hide Preview' : 'Preview Draft'}
            </button>
            <button onClick={saveDraft} disabled={saving} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="brand-save-draft-button">
              {saving ? 'Saving Draft...' : 'Save Draft'}
            </button>
            <button onClick={publish} disabled={publishing} className="rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="brand-publish-live-button">
              {publishing ? 'Publishing...' : 'Publish Live'}
            </button>
            <button onClick={revert} disabled={reverting} className="rounded-full border border-red-300 px-5 py-3 text-sm font-semibold text-red-600 disabled:opacity-50" data-testid="brand-revert-button">
              {reverting ? 'Reverting...' : 'Revert to Published'}
            </button>
          </div>
        </div>
        {message && <p className="mt-4 text-sm text-slate-700" data-testid="brand-content-save-message">{message}</p>}
      </div>

      {showPreview && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm" data-testid="brand-preview-panel">
          <h4 className="text-lg font-bold text-slate-900">Draft Preview</h4>
          <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200">
            <div className="relative bg-slate-950 text-white p-8">
              {previewContent.about_hero_image && <img src={previewContent.about_hero_image} alt="About hero" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
              <div className="relative z-10">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/60">About Preview</p>
                <h2 className="mt-3 text-4xl font-black">{previewContent.about_hero_title}</h2>
                <p className="mt-4 max-w-3xl text-white/80">{previewContent.about_hero_text}</p>
              </div>
            </div>
            <div className="p-6 space-y-6 bg-slate-50">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {(previewContent.home_about_cards || []).map((card, idx) => (
                  <div key={idx} className="rounded-2xl bg-white p-5 shadow-sm border border-slate-200">
                    <div className="text-2xl">{card.icon}</div>
                    <h5 className="mt-3 font-bold text-slate-900">{card.title}</h5>
                    <p className="mt-2 text-sm text-slate-500">{card.text}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-2xl bg-slate-900 p-5 text-white"><h5 className="font-bold">{previewContent.vision_title}</h5><p className="mt-2 text-sm text-white/75">{previewContent.vision_body}</p></div>
                <div className="rounded-2xl bg-orange-500 p-5 text-white"><h5 className="font-bold">{previewContent.mission_title}</h5><p className="mt-2 text-sm text-white/85">{previewContent.mission_body}</p></div>
              </div>
              <div className="rounded-2xl bg-white p-5 border border-slate-200">
                <h5 className="font-bold text-slate-900">Founder Story</h5>
                {(previewContent.founder_name || previewContent.founder_title) && (
                  <p className="mt-1 text-xs font-semibold text-orange-600">{previewContent.founder_name}{previewContent.founder_title ? ` · ${previewContent.founder_title}` : ''}</p>
                )}
                <p className="mt-2 text-sm text-slate-500">{previewContent.founder_story}</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {(previewContent.team_members || []).map((member, idx) => (
                  <div key={idx} className="rounded-2xl bg-white p-5 border border-slate-200">
                    <div className="h-12 w-12 rounded-xl bg-slate-800 text-white flex items-center justify-center text-lg font-bold">{((member.name || member.role || '?').trim().charAt(0) || '?').toUpperCase()}</div>
                    {member.name && <h5 className="mt-3 font-bold text-slate-900">{member.name}</h5>}
                    <p className={`${member.name ? 'mt-0.5 text-orange-600 text-xs font-semibold' : 'mt-3 font-bold text-slate-900'}`}>{member.role}</p>
                    <p className="mt-2 text-sm text-slate-500">{member.summary}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm space-y-5">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">About Hero Title</label>
          <input value={form.about_hero_title} onChange={(e) => setField('about_hero_title', e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3" data-testid="brand-about-hero-title-input" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">About Hero Text</label>
          <textarea value={form.about_hero_text} onChange={(e) => setField('about_hero_text', e.target.value)} rows={4} className="w-full rounded-xl border border-slate-300 px-4 py-3" data-testid="brand-about-hero-text-input" />
        </div>
        {renderImageUpload({ value: form.about_hero_image, onChange: (url) => setField('about_hero_image', url), uploadId: 'about-hero-image', label: 'About Hero Image' })}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Vision Title</label>
            <input value={form.vision_title} onChange={(e) => setField('vision_title', e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Mission Title</label>
            <input value={form.mission_title} onChange={(e) => setField('mission_title', e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Vision Body</label>
            <textarea value={form.vision_body} onChange={(e) => setField('vision_body', e.target.value)} rows={4} className="w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Mission Body</label>
            <textarea value={form.mission_body} onChange={(e) => setField('mission_body', e.target.value)} rows={4} className="w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Founder / Team Name</label>
            <input value={form.founder_name} onChange={(e) => setField('founder_name', e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3" data-testid="brand-founder-name-input" placeholder="e.g. System Of Success" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Founder Title / Designation</label>
            <input value={form.founder_title} onChange={(e) => setField('founder_title', e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3" data-testid="brand-founder-title-input" placeholder="e.g. Founder & CEO — शिक्षा मित्र" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Founder Story</label>
          <textarea value={form.founder_story} onChange={(e) => setField('founder_story', e.target.value)} rows={5} className="w-full rounded-xl border border-slate-300 px-4 py-3" data-testid="brand-founder-story-input" />
        </div>
      </div>

      {renderListEditor('home_about_cards', form.home_about_cards || [])}
      {renderListEditor('values_cards', form.values_cards || [])}
      {renderListEditor('team_members', form.team_members || [])}
      {renderListEditor('milestones', form.milestones || [])}
      {renderListEditor('awards', form.awards || [])}
      {renderListEditor('media_mentions', form.media_mentions || [])}
    </div>
  );
};
